import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type {
  PricingConfigurationInput,
  PricingConfigurationPreviewResponse,
  PricingConfigurationValidationResponse,
  PricingOwnershipAdoptionRequest,
} from '@/shared/api';
import type { SaveResult } from './pricingConfigurationRequests';
import { toApiInput, type PricingConfigurationFormValues } from './pricingConfigurationSchema';

interface Options {
  form: UseFormReturn<PricingConfigurationFormValues>;
  configuration: PricingConfigurationInput;
  saveErrorCode?: string;
  /** Lesende Serverprüfung; ohne sie gehen Vorschau und „Aus speichern“ direkt los. */
  onValidate?: (
    configuration: PricingConfigurationInput,
  ) => Promise<PricingConfigurationValidationResponse | undefined>;
  onPreview?: (
    configuration: PricingConfigurationInput,
  ) => Promise<PricingConfigurationPreviewResponse | undefined>;
  onSave: (
    configuration: PricingConfigurationInput,
    previewAttestationId?: string,
    ownershipAdoption?: PricingOwnershipAdoptionRequest,
    previewedAt?: string,
  ) => SaveResult | boolean | void | Promise<SaveResult | boolean | void>;
  /** Formular oder Server haben etwas beanstandet: der Aufrufer zeigt, wo. */
  onRejected?: (source: 'client' | 'server') => void;
}

const emptyAdoption = (): PricingOwnershipAdoptionRequest => ({ rate: false, minimumStay: false });

export function usePricingPreviewFlow(options: Options) {
  const [dialogOpen, setDialogOpenState] = useState(false);
  const [preview, setPreview] = useState<PricingConfigurationPreviewResponse>();
  const [previewInput, setPreviewInput] = useState<PricingConfigurationInput>();
  const [previewKey, setPreviewKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ownershipAdoption, setOwnershipAdoption] = useState(emptyAdoption);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const confirmingRef = useRef(false);
  const values = options.form.watch();
  const draftKey = JSON.stringify(values);
  const freshPreview = previewKey === draftKey ? preview : undefined;

  useEffect(() => {
    setPreview(undefined);
    setPreviewInput(undefined);
    setPreviewKey('');
  }, [options.configuration]);
  useEffect(() => {
    if (options.saveErrorCode?.startsWith('pricing_preview_attestation_')) clearPreview();
  }, [options.saveErrorCode]);

  const createInput = (formValues: PricingConfigurationFormValues) =>
    toApiInput(formValues, options.configuration);
  const rejected = () => options.onRejected?.('client');
  /**
   * Ohne Freigabe des Servers öffnet sich keine Vorschau. Eine offene schließt sich, damit Befund
   * oder Fehler neben dem Formular sichtbar werden statt hinter dem Dialog.
   */
  const serverAccepts = async (input: PricingConfigurationInput) => {
    if (!options.onValidate) return true;
    const result = await options.onValidate(input);
    const ok = Boolean(result?.canSavePaused && result.isPreviewReady);
    if (!ok) {
      setDialogOpen(false);
      options.onRejected?.('server');
    }
    return ok;
  };
  const runPreview = options.form.handleSubmit(async (formValues) => {
    const current = createInput(formValues);
    if (!(await serverAccepts(current))) return;
    setDialogOpenState(true);
    setPreview(undefined);
    setOwnershipAdoption(emptyAdoption());
    setPreviewInput(current);
    const result = await options.onPreview?.(current);
    if (result) {
      setPreview(result);
      setPreviewKey(JSON.stringify(formValues));
    }
  }, rejected);
  const openPreview = () => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    void runPreview();
  };
  // „Aus“ prüft der Server beim Speichern selbst (`save`); eine Ablehnung zeigt ihre Befunde.
  const savePaused = options.form.handleSubmit(async (formValues) => {
    const result = await options.onSave(createInput(formValues), undefined);
    if (result === 'rejected') options.onRejected?.('server');
  }, rejected);
  const confirm = async () => {
    if (confirmingRef.current || !freshPreview?.previewAttestationId || !previewInput) return;
    confirmingRef.current = true;
    setSubmitting(true);
    try {
      const saved = await options.onSave(
        previewInput,
        freshPreview.previewAttestationId,
        ownershipAdoption,
        freshPreview.calculatedAtUtc,
      );
      clearPreview();
      // Lehnt die Prüfung beim Aktivieren ab, stehen die Befunde hinter dem Dialog – er schließt.
      // Ohne Antwort schließt er auch: was gilt, zeigt der neu geladene Stand auf der Seite.
      if (saved === 'rejected') {
        setDialogOpenState(false);
        options.onRejected?.('server');
      } else if (saved) setDialogOpenState(false);
    } finally {
      confirmingRef.current = false;
      setSubmitting(false);
    }
  };
  function clearPreview() {
    setPreview(undefined);
    setPreviewInput(undefined);
    setPreviewKey('');
    setOwnershipAdoption(emptyAdoption());
  }
  function setDialogOpen(open: boolean) {
    setDialogOpenState(open);
    if (!open) clearPreview();
  }

  return {
    values,
    dialogOpen,
    setDialogOpen,
    freshPreview,
    previewInput,
    submitting,
    ownershipAdoption,
    setOwnershipAdoption,
    returnFocusRef,
    openPreview,
    runPreview,
    savePaused,
    confirm,
  };
}
